import { useState } from 'react'
import { useNavigate } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'
import { idempotencyKey } from '@/lib/idempotency'

type Tier = 'starter' | 'growth' | 'scale'
type Plan = Schema<'PlanOut'>
type Preview = Schema<'PreviewResponse'>
type Subscription = Schema<'SubscriptionOut'>

/**
 * Which card carries "Most popular" and the primary button (ADM-1).
 *
 * A constant rather than three inline `=== 'growth'` comparisons, because the
 * tag, the border and the button tone are one claim made three times and a
 * screen that highlighted one card and recommended another would be worse than
 * one that recommended nothing.
 */
const RECOMMENDED: Tier = 'growth'

/**
 * `admin#plans` and `admin#checkout`, on S-B6's endpoints.
 *
 * **The currency toggle is real, not decorative**, which is what that screen's
 * *"why this shape"* note asks for: *"a tenant seeing ₹ when they expected $
 * needs to fix it in one click, not email support"*. Switching re-fetches, so
 * the prices shown are §11's two price sets rather than a conversion done in
 * the browser — and the caption says how the default was arrived at, because a
 * tenant told they were "detected" when nothing was detected has been told
 * something false about their own data.
 *
 * **The five limits are the whole list.** S-B12a found twelve invented details
 * on this screen — keyword caps, an included usage cap, "priority support", a
 * fourth SKU — and removed all of them. What is rendered here is exactly what
 * `GET /billing/plans` returns, which is exactly REQUIREMENTS §11's table.
 *
 * **Checkout is a top-level navigation to the provider's hosted page.** No card
 * field appears in AugRank's DOM: that keeps AugRank outside PCI DSS scope, and
 * it is the only place an Indian e-mandate can be authenticated at all.
 *
 * **A plan change now goes through a confirmation (ADM-5, ADM-6).** It used to
 * post `POST /billing/subscription/plan` from the card's own button and toast
 * *"Your plan has been changed"* — a tenant pressed one button and was charged a
 * prorated amount they had never been shown. The screen the design draws for
 * this is `admin#checkout`, and `GET /billing/subscription/preview` is the
 * endpoint that answers it: what it costs, when it takes effect, and what it
 * would break.
 */
export function PlansPage(): JSX.Element {
  const { membership } = useActiveTenant()
  const toast = useToast()
  const navigate = useNavigate()
  const [currency, setCurrency] = useState<'INR' | 'USD' | null>(null)
  // What the tenant is confirming — the plan **and** the quote it was made
  // against — or `null` for the ladder. Held together rather than separately so
  // the card can never render one plan's name over another plan's total, and
  // held here rather than routed to `/billing/checkout`, because a reloadable
  // URL would show a price nobody had agreed to re-quote.
  const [confirming, setConfirming] = useState<{ plan: Plan; quote: Preview } | null>(null)
  const manages = may(membership, 'billing:manage')

  const plans = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/billing/plans', {
          params: { query: currency ? { currency } : {} },
          signal,
        }),
      ),
    [currency],
  )
  const current = useQuery(
    (signal) => call(() => api.GET('/api/v1/billing/subscription', { signal })),
    [],
  )

  const start = useMutation((plan: Tier) =>
    call(() =>
      api.POST('/api/v1/billing/subscription', {
        body: { plan, currency: currency },
        params: { header: { 'Idempotency-Key': idempotencyKey('checkout', plan) } },
      }),
    ),
  )
  const preview = useMutation((plan: Tier) =>
    call(() => api.GET('/api/v1/billing/subscription/preview', { params: { query: { plan } } })),
  )
  const change = useMutation((input: { plan: Tier; acknowledge: boolean }) =>
    call(() =>
      api.POST('/api/v1/billing/subscription/plan', {
        body: { plan: input.plan, acknowledge_conflicts: input.acknowledge },
        params: { header: { 'Idempotency-Key': idempotencyKey('plan', input.plan) } },
      }),
    ),
  )

  if (plans.state.status === 'loading') return <Loading label="Loading plans" />
  if (plans.state.status === 'error') {
    return <ErrorState failure={plans.state.failure} onRetry={plans.refetch} />
  }

  const ladder = plans.state.data
  const subscription = current.state.data?.subscription ?? null
  const currentRank =
    ladder.plans.find((plan) => plan.code === subscription?.plan_code)?.rank ?? null

  async function choose(plan: Plan): Promise<void> {
    if (subscription) {
      // The preview first, and the change only after the tenant has seen it.
      const quoted = await preview.run(plan.code as Tier)
      if (quoted.ok) setConfirming({ plan, quote: quoted.data })
      return
    }
    const result = await start.run(plan.code as Tier)
    if (result.ok) {
      // A top-level navigation rather than an iframe: the edge sets
      // `frame-ancestors 'none'`, and a payment page inside a frame is the
      // shape phishing takes anyway.
      window.location.assign(result.data.checkout_url)
    }
  }

  async function commit(plan: Plan, quoted: Preview): Promise<void> {
    // **`acknowledge_conflicts` is true only when conflicts were rendered.**
    // The flag says the tenant *saw* them (NC-2); hard-coding it — which is what
    // this screen used to do with `false` — makes it say nothing at all, and
    // sending `true` unprompted would be a client asserting a conversation that
    // never happened. The server still evaluates the conflicts either way.
    const result = await change.run({
      plan: plan.code as Tier,
      acknowledge: (quoted.conflicts ?? []).length > 0,
    })
    if (!result.ok) return
    setConfirming(null)
    toast.show({
      kind: 'success',
      title: quoted.upgrade
        ? `You are on ${plan.name}.`
        : `You move to ${plan.name} at the end of this period.`,
    })
    void navigate('/billing')
  }

  if (confirming !== null) {
    const { plan, quote } = confirming
    return (
      <ConfirmChange
        plan={plan}
        preview={quote}
        subscription={subscription}
        pending={change.pending}
        failure={change.state.failure}
        onCancel={() => {
          setConfirming(null)
          change.reset()
        }}
        onConfirm={() => {
          void commit(plan, quote)
        }}
      />
    )
  }

  return (
    <div className="ar-stack ar-stack--loose" style={{ maxWidth: '64rem', margin: '0 auto' }}>
      <div style={{ textAlign: 'center' }}>
        <h1 className="ar-display">Simple pricing</h1>
        <p className="ar-muted" style={{ maxWidth: '44ch', margin: 'var(--ar-space-3) auto 0' }}>
          SEO and outreach together. No separate SKUs, no per-seat pricing.
        </p>
        <div
          className="ar-row"
          style={{ justifyContent: 'center', marginTop: 'var(--ar-space-5)' }}
        >
          <div className="app-seg" style={{ width: 'auto' }} role="group" aria-label="Currency">
            <button
              type="button"
              aria-pressed={ladder.currency === 'INR'}
              onClick={() => {
                setCurrency('INR')
              }}
            >
              ₹ INR
            </button>
            <button
              type="button"
              aria-pressed={ladder.currency === 'USD'}
              onClick={() => {
                setCurrency('USD')
              }}
            >
              $ USD
            </button>
          </div>
          <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
            {describeSignal(ladder.signal, ladder.country)}
          </span>
        </div>
      </div>

      <InlineError failure={start.state.failure ?? preview.state.failure ?? change.state.failure} />

      <div className="ad-plans">
        {ladder.plans.map((plan) => {
          const isCurrent = subscription?.plan_code === plan.code
          return (
            <div
              className={[
                'ar-card',
                'ad-plan',
                plan.code === RECOMMENDED ? 'ad-plan--pick' : null,
                // ADM-21. What lets the current plan's button render at full
                // strength without becoming pressable.
                isCurrent ? 'ad-plan--current' : null,
              ]
                .filter(Boolean)
                .join(' ')}
              key={plan.code}
              // ADM-10's "link straight to it": the overage prompt names a tier
              // and this is what its button can aim at, rather than dropping the
              // reader at the top of a three-card ladder to find it again.
              id={`plan-${plan.code}`}
            >
              {/* ADM-1. Absolutely positioned over the card's top edge, which is
                  what `.ad-plan` exists as a positioning context for — the rule
                  was ported in S-B27 and nothing has ever emitted the element. */}
              {plan.code === RECOMMENDED ? (
                <span className="ad-plan__tag ar-badge ar-badge--brand">Most popular</span>
              ) : null}
              <div className="ar-card__header">
                <h2>{plan.name}</h2>
                {isCurrent ? (
                  <>
                    <span className="ar-card__spacer" />
                    <span className="ar-badge ar-badge--success">Current</span>
                  </>
                ) : null}
              </div>
              <div className="ar-card__body ar-stack">
                <div>
                  <span className="ad-plan__price">{plan.price.display}</span>{' '}
                  <span className="ar-muted">/month</span>
                </div>
                <ul>
                  <li>
                    {plan.sites} {plan.sites === 1 ? 'site' : 'sites'}
                  </li>
                  <li>
                    {plan.linkedin_accounts} LinkedIn{' '}
                    {plan.linkedin_accounts === 1 ? 'account' : 'accounts'}
                  </li>
                  <li>{plan.messages_per_month.toLocaleString()} messages a month</li>
                  <li>{plan.articles_per_month} articles a month</li>
                  {/* ADM-2 / NC-1. The channels bullet, and the only one on the
                      card that is **not** a limit: every tier sends on the same
                      channels, which is why it comes from the ladder rather than
                      from `plan`, and why it stays *Email and WhatsApp* on every
                      card where `admin.html#plans` writes *unified inbox, and
                      CRM* on Growth and *Everything in Growth* on Scale — the
                      inbox and the pipeline are on every plan, and a bullet that
                      said otherwise would be the over-promise the mockup once
                      made with SMS, RCS and IVR. Its place is the mockup's:
                      before the discovery cadence (parity P-PLAN-05). */}
                  {ladder.outreach_channels.length === 0 ? null : (
                    <li>{listOf(ladder.outreach_channels)}</li>
                  )}
                  <li>{discovery(plan.scrapes_per_week)}</li>
                </ul>
              </div>
              <div className="ar-card__footer">
                <Button
                  // Only the current plan's button is primary (parity P-PLAN-06);
                  // the recommended tier is marked by its tag, not a second
                  // orange button.
                  tone={isCurrent ? 'primary' : 'ghost'}
                  block
                  disabled={
                    isCurrent ||
                    !manages ||
                    !plan.available ||
                    start.pending ||
                    preview.pending ||
                    change.pending
                  }
                  onClick={() => {
                    void choose(plan)
                  }}
                >
                  {buttonLabel(plan, isCurrent, currentRank)}
                </Button>
              </div>
            </div>
          )
        })}
      </div>

      <p
        className="ar-subtle"
        style={{ textAlign: 'center', fontSize: 'var(--ar-text-body-sm-size)' }}
      >
        The five limits above are the whole list — there are no others. Rank data, model calls,
        images, and proxy bandwidth are metered and run against the monthly budget cap <em>you</em>{' '}
        set in Settings, which starts at zero risk: when the cap is reached everything pauses rather
        than billing on. INR prices exclude {ladder.gst_percent}% GST. USD is billed by Paddle as
        merchant of record, which charges and remits your local tax itself.
        {/* Two sentences the drawing has no room for and a tenant has to know:
            the e-mandate ceiling on rupee renewals, and the trial. */}
        {ladder.currency === 'INR' ? (
          <>
            {' '}
            Renewals above ₹{(ladder.emandate_ceiling_minor / 100).toLocaleString('en-IN')} need
            your bank&apos;s approval each month, which we will remind you about 24 hours before.
          </>
        ) : null}{' '}
        Every plan starts with a {ladder.trial_days}-day trial.
      </p>
      {!manages ? (
        <p className="ar-subtle" style={{ textAlign: 'center' }}>
          Only the organisation&apos;s owner can start or change a subscription.
        </p>
      ) : null}
    </div>
  )
}

/**
 * `admin#checkout` — "Confirm your plan change" (ADM-5, ADM-6).
 *
 * **The figures are the server's, not the browser's.** Proration is a function
 * of the exact instant the change is made, the days already paid for and the
 * tenant's GSTIN; `GET /billing/subscription/preview` computes it with the same
 * code `POST /subscription/plan` will charge with, so the number on this card
 * and the number on the invoice cannot disagree. Nothing here multiplies
 * anything.
 *
 * **The payment-method line is read-only, and the mockup's `<select>` is not
 * built** (NC-3). Its options were "HDFC •••• 4242", "UPI", "Net banking" and
 * "Add a new card": choosing between stored methods and adding one both happen
 * on the provider's hosted page, which is where every card field in this product
 * lives. What is shown is the provider's own masked summary of the method that
 * will be debited — brand and last four, nothing else.
 *
 * **A downgrade renders no money at all.** It charges nothing and refunds
 * nothing; what it does is take effect later, so the card states the date
 * instead of a total. Showing a ₹0 line item would invite the reader to wonder
 * what they are being charged zero for.
 */
function ConfirmChange({
  plan,
  preview,
  subscription,
  pending,
  failure,
  onCancel,
  onConfirm,
}: {
  plan: Plan
  preview: Preview
  subscription: Subscription | null
  pending: boolean
  failure: Parameters<typeof InlineError>[0]['failure']
  onCancel: () => void
  onConfirm: () => void
}): JSX.Element {
  // `?? []` because the field carries a server-side default, which OpenAPI
  // renders as optional — the same absence every other generated optional has.
  const conflicts = preview.conflicts ?? []
  // *For the rest of July* (parity P-CHK-02): the month the current period
  // ends in, which is what the credit is for; *this period* when the
  // subscription does not say when it ends.
  const periodMonth = subscription?.current_period_end
    ? new Intl.DateTimeFormat('en-GB', { month: 'long', timeZone: 'Asia/Kolkata' }).format(
        new Date(subscription.current_period_end),
      )
    : null
  return (
    // The card is the whole screen (parity P-CHK-03): `admin#checkout` draws
    // it alone, centred, with no shell around it. A fixed takeover over the
    // shell rather than a route of its own, because the quote and the plan
    // are this page's state — a URL could only reach the card with nothing
    // on it — and a dialog is what a Pay button with a Cancel beside it is.
    <div
      className="app-takeover"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-plan-change"
    >
      <div className="app-takeover__body">
        <div className="ar-card">
          <div className="ar-card__header">
            <h2 id="confirm-plan-change">Confirm your plan change</h2>
          </div>
          <div className="ar-card__body ar-stack">
            <div className="ar-panel ar-stack ar-stack--tight">
              <div className="ar-row" style={{ justifyContent: 'space-between' }}>
                <span>
                  {subscription ? `${subscription.plan_name} → ` : ''}
                  {plan.name}
                </span>
                {/* The **preview's** price, not the ladder's. Every other figure
                  on this card is in the subscription's currency, and the ladder
                  is in whatever the currency toggle is on — so a tenant billed
                  in rupees who had pressed "$ USD" saw "$699/mo" above "Charged
                  today ₹57,936.82". One card, one currency. */}
                <span className="ar-mono">{preview.plan_display}/mo</span>
              </div>
              {preview.upgrade ? (
                <>
                  <div className="ar-row" style={{ justifyContent: 'space-between' }}>
                    <span className="ar-muted">
                      Prorated credit for the rest of {periodMonth ?? 'this period'}
                    </span>
                    <span className="ar-mono" style={{ color: 'var(--ar-success)' }}>
                      −{preview.credit_display}
                    </span>
                  </div>
                  {preview.tax_minor > 0 ? (
                    <div className="ar-row" style={{ justifyContent: 'space-between' }}>
                      <span className="ar-muted">GST {preview.gst_percent}%</span>
                      <span className="ar-mono">{preview.tax_display}</span>
                    </div>
                  ) : null}
                  <hr />
                  <div className="ar-row" style={{ justifyContent: 'space-between' }}>
                    <strong>Charged today</strong>
                    <strong className="ar-mono">{preview.total_display}</strong>
                  </div>
                </>
              ) : null}
            </div>

            {/* ADM-6 / NC-2. The conflicts view. `acknowledge_conflicts` has been
              documented since S-B6 as "set only after the tenant has been shown
              the conflicts and accepted them", and until now nothing showed
              them: a blocked downgrade surfaced as a bare error with no way
              forward, so the flag could only ever be false. Each label is the
              server's sentence — the numbers and the noun have to agree, and
              splitting them is how "3 sites" becomes "3 LinkedIn accounts". */}
            {conflicts.length > 0 ? (
              <div className="ar-alert ar-alert--warning">
                <span className="ar-alert__icon" aria-hidden="true">
                  ⚠
                </span>
                <div className="ar-alert__body">
                  <strong>{plan.name} is below what you are using</strong>
                  <ul
                    style={{
                      margin: 'var(--ar-space-2) 0 0',
                      paddingLeft: 'var(--ar-space-5)',
                      display: 'grid',
                      gap: 'var(--ar-space-1)',
                    }}
                  >
                    {conflicts.map((conflict) => (
                      <li key={conflict.key}>{conflict.label}</li>
                    ))}
                  </ul>
                  <p style={{ margin: 'var(--ar-space-2) 0 0' }}>
                    Nothing is deleted. What is over the new limit stops working until you bring it
                    down — so it is worth doing that first if you can.
                  </p>
                </div>
              </div>
            ) : (
              <div className="ar-alert ar-alert--info">
                <span className="ar-alert__icon" aria-hidden="true">
                  ◷
                </span>
                <div className="ar-alert__body">
                  {preview.upgrade ? (
                    <>
                      <strong>Your new limits apply immediately</strong> {plan.sites}{' '}
                      {plan.sites === 1 ? 'site' : 'sites'} and {plan.articles_per_month} articles a
                      month unlock as soon as the payment clears. Nothing is lost in the switch.
                    </>
                  ) : (
                    <>
                      <strong>
                        {plan.name} starts on {formatEffective(preview.effective_at)}
                      </strong>{' '}
                      You keep everything you have until then — it is the period you have already
                      paid for — and nothing is charged today.
                    </>
                  )}
                </div>
              </div>
            )}

            {/* ADM-7 / NC-3. Display-only. The mockup's selector is not built: both
              of the things it offered happen on the provider's own page. */}
            {subscription?.payment_method ? (
              <div className="ar-row" style={{ justifyContent: 'space-between' }}>
                <span className="ar-muted">Payment method</span>
                <span>{subscription.payment_method.display}</span>
              </div>
            ) : null}

            <InlineError failure={failure} />
          </div>
          <div className="ar-card__footer">
            <Button tone="ghost" onClick={onCancel}>
              Cancel
            </Button>
            <span className="ar-card__spacer" />
            <Button tone="primary" disabled={pending} busy={pending} onClick={onConfirm}>
              {preview.upgrade ? `Pay ${preview.total_display}` : `Move to ${plan.name}`}
            </Button>
          </div>
        </div>
        <p
          className="ar-subtle"
          style={{
            textAlign: 'center',
            marginTop: 'var(--ar-space-4)',
            fontSize: 'var(--ar-text-caption-size)',
          }}
        >
          {/* The mockup hardcodes "Processed by Razorpay", which is true of the INR
            path and wrong for every dollar customer. The subscription knows. */}
          Processed by {subscription?.provider === 'paddle' ? 'Paddle' : 'Razorpay'}. We never store
          your card details.
        </p>
      </div>
    </div>
  )
}

/** The effective date of a deferred change, in the reader's own locale. */
function formatEffective(iso: string): string {
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return 'the end of this period'
  return parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

/**
 * The channel bullet — *"Email and WhatsApp"*.
 *
 * **Written as a rule rather than a `join(' and ')`, because two is a fact about
 * today.** The list comes off `app/outreach/channels.CATALOGUE` and has been six,
 * five and three inside a month; a two-item join renders *"Email, WhatsApp,
 * SMS"* the day a third returns, which reads as a truncated list rather than a
 * complete one. This is the same reason the plural rules on the bullets above it
 * are conditionals and not assumptions.
 *
 * An empty list renders nothing rather than an empty bullet, which is the honest
 * output for a deployment whose catalogue offers no sendable channel at all.
 */
function listOf(names: string[]): string {
  // Read the last element first and narrow on it: `noUncheckedIndexedAccess`
  // types every index access as possibly-undefined, and an emptiness check the
  // compiler cannot connect to the access does not narrow it.
  const last = names[names.length - 1]
  if (last === undefined) return ''
  if (names.length === 1) return last
  return `${names.slice(0, -1).join(', ')} and ${last}`
}

/**
 * The caption beside the toggle. `admin#plans` prints "Detected: India"; this
 * says that only when something actually was detected.
 *
 * **A manual switch no longer erases the detection** (ADM-4). It used to print a
 * bare "Your choice", because the API dropped the country the moment a currency
 * was requested — so the one screen state where a tenant might be *querying*
 * what AugRank thinks it knows about them was the state that stopped saying.
 */
function describeSignal(signal: string, country: string | null | undefined): string {
  if (signal === 'edge' && country) return `Detected: ${countryName(country)}`
  if (signal === 'mobile') return 'Based on your mobile number'
  if (signal === 'requested') {
    return country ? `Your choice — detected: ${countryName(country)}` : 'Your choice'
  }
  return 'Showing rupees by default'
}

/** *India*, not *IN* (parity P-PLAN-03); the code itself when `Intl` has no name for it. */
function countryName(code: string): string {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code.toUpperCase()) ?? code
  } catch {
    return code
  }
}

/** *Lead discovery twice a week* / *Daily lead discovery*, as the cards spell the cadence. */
function discovery(perWeek: number): string {
  if (perWeek >= 7) return 'Daily lead discovery'
  if (perWeek === 2) return 'Lead discovery twice a week'
  if (perWeek === 1) return 'Lead discovery once a week'
  return `Lead discovery ${String(perWeek)}× a week`
}

/**
 * What the card's button says (ADM-3).
 *
 * **Direction-aware, because "Move to Starter" is the one wording that hides
 * what the button does.** The mockup says "Upgrade to Scale"; the app said
 * "Move to" for every tier in both directions, which reads identically on the
 * card that costs more and the card that takes limits away. Rank comes off the
 * ladder rather than from an ordering assumed here, so a fourth tier inserted in
 * the middle is labelled correctly with no change to this function.
 *
 * `currentRank` is null before the subscription has loaded, and the neutral
 * wording is deliberate: a label that guessed a direction and then changed under
 * the reader is worse than one that waits.
 */
function buttonLabel(plan: Plan, isCurrent: boolean, currentRank: number | null): string {
  if (isCurrent) return 'Your current plan'
  if (!plan.available) return 'Not available yet'
  if (currentRank === null) return `Choose ${plan.name}`
  return plan.rank > currentRank ? `Upgrade to ${plan.name}` : `Downgrade to ${plan.name}`
}
